import type { GetApplicationAnalyticsResponseI } from "@Common/types/bo/applications/applications";
import { httpClient } from "@Src/dataProvider";
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Grid,
	List,
	ListItem,
	ListItemText,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNotify } from "react-admin";

interface ApplicationAnalyticsProps {
	applicationId: number;
}

interface DistributionItemProps {
	label: string;
	count: number;
	isHighlighted: boolean;
}

const DistributionItem = ({
	label,
	count,
	isHighlighted,
}: DistributionItemProps) => {
	return (
		<ListItem
			sx={{
				backgroundColor: isHighlighted ? "action.selected" : "transparent",
				borderLeft: isHighlighted ? "4px solid primary.main" : "none",
				pl: isHighlighted ? 1.5 : 2,
			}}
		>
			<ListItemText
				primary={label}
				secondary={`Count: ${count}`}
				primaryTypographyProps={{
					fontWeight: isHighlighted ? "bold" : "normal",
				}}
			/>
		</ListItem>
	);
};

const GeographicDistribution = ({
	data,
	currentOrigin,
}: {
	data: GetApplicationAnalyticsResponseI["geographicDistribution"];
	currentOrigin: string | null;
}) => {
	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Geographic Distribution
				</Typography>
				<List dense>
					{data.map((item) => (
						<DistributionItem
							key={item.origin}
							label={item.origin}
							count={item.count}
							isHighlighted={item.origin === currentOrigin}
						/>
					))}
				</List>
			</CardContent>
		</Card>
	);
};

const BrowserDistribution = ({
	data,
	currentUserAgent,
}: {
	data: GetApplicationAnalyticsResponseI["browserDistribution"];
	currentUserAgent: string | null;
}) => {
	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Browser Distribution
				</Typography>
				<List dense>
					{data.map((item, index) => (
						<DistributionItem
							key={`${item.userAgent}-${index}`}
							label={
								item.userAgent.length > 60
									? `${item.userAgent.substring(0, 60)}...`
									: item.userAgent
							}
							count={item.count}
							isHighlighted={item.userAgent === currentUserAgent}
						/>
					))}
				</List>
			</CardContent>
		</Card>
	);
};

const CompletionTimeDistribution = ({
	data,
	currentHour,
}: {
	data: GetApplicationAnalyticsResponseI["completionTimeDistribution"];
	currentHour: number | null;
}) => {
	const formatHourRange = (hour: number) => {
		const start = hour.toString().padStart(2, "0");
		const end = ((hour + 1) % 24).toString().padStart(2, "0");
		return `${start}:00 - ${end}:00 UTC`;
	};

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Completion Time Distribution (Hour of Day)
				</Typography>
				<List dense sx={{ maxHeight: 400, overflow: "auto" }}>
					{data.map((item) => (
						<DistributionItem
							key={item.hour}
							label={formatHourRange(item.hour)}
							count={item.count}
							isHighlighted={item.hour === currentHour}
						/>
					))}
				</List>
			</CardContent>
		</Card>
	);
};

const TimeToCompleteDistribution = ({
	data,
	currentBucket,
}: {
	data: GetApplicationAnalyticsResponseI["timeToCompleteDistribution"];
	currentBucket: string | null;
}) => {
	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Time to Complete Distribution
				</Typography>
				<List dense>
					{data.map((item) => (
						<DistributionItem
							key={item.bucket}
							label={item.bucket}
							count={item.count}
							isHighlighted={item.bucket === currentBucket}
						/>
					))}
				</List>
			</CardContent>
		</Card>
	);
};

export const ApplicationAnalytics = ({
	applicationId,
}: ApplicationAnalyticsProps) => {
	const [loading, setLoading] = useState(true);
	const [analyticsData, setAnalyticsData] =
		useState<GetApplicationAnalyticsResponseI | null>(null);
	const notify = useNotify();

	useEffect(() => {
		const fetchAnalytics = async () => {
			try {
				setLoading(true);
				const response = await httpClient(
					`/applications/applications/${applicationId}/analytics`,
				);
				setAnalyticsData(response.json as GetApplicationAnalyticsResponseI);
			} catch (error) {
				notify("Failed to load analytics", { type: "error" });
				console.error("Failed to fetch analytics:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchAnalytics();
	}, [applicationId, notify]);

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" p={4}>
				<CircularProgress />
			</Box>
		);
	}

	if (!analyticsData) {
		return (
			<Box p={2}>
				<Typography color="error">Failed to load analytics data</Typography>
			</Box>
		);
	}

	return (
		<Box mt={4}>
			<Typography variant="h5" gutterBottom>
				Analytics Comparison
			</Typography>
			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<GeographicDistribution
						data={analyticsData.geographicDistribution}
						currentOrigin={analyticsData.currentApplicationData.request_origin}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<BrowserDistribution
						data={analyticsData.browserDistribution}
						currentUserAgent={
							analyticsData.currentApplicationData.browser_user_agent
						}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<CompletionTimeDistribution
						data={analyticsData.completionTimeDistribution}
						currentHour={analyticsData.currentApplicationData.submission_hour}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<TimeToCompleteDistribution
						data={analyticsData.timeToCompleteDistribution}
						currentBucket={
							analyticsData.currentApplicationData.time_to_complete_bucket
						}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};
